<template>
  <div>
    <!-- View -->
    <a-modal
      v-model="visible"
      title="Student Information"
      :width="
        this.settings['isMobileView']
          ? 480
          : this.settings['isTabletView']
          ? 720
          : 1080
      "
      style="top: 20px"
    >
      <template slot="footer">
        <div>
          <a-button key="back" @click="handleCancel">Close</a-button>
        </div>
      </template>
      <a-skeleton
        active
        :title="false"
        :paragraph="{ rows: 20 }"
        :loading="loading"
      />
      <div v-if="!loading">
        <div class="row mb-4">
          <!-- LEFT CARD: Avatar + Name + Student Code Badge etc. (col-md-4) -->
          <div class="col-12 col-md-4 mb-3">
            <a-card bordered>
              <div class="text-center">
                <!-- Avatar centered at top -->
                <a-avatar
                  shape="square"
                  :size="150"
                  :icon="studentData.photo ? '' : 'user'"
                  :src="studentData.photo"
                />
              </div>
              <div class="mt-3 text-center">
                <!-- Student Full Name -->
                <p class="h4 font-weight-bold mb-1 text-truncate">
                  {{ studentData.first_name }} {{ studentData.last_name }}
                </p>

                <p class="font-weight-normal mb-2">
                  <strong>Student Code: {{ studentData.student_code }}</strong>
                </p>

                <!-- First Row -->
                <div class="row justify-content-center mb-2">
                  <!-- Class -->
                  <div class="col-5 col-md-4 mb-3">
                    <div class="mb-1">Class:</div>
                    <div class="font-weight-bold">
                      {{
                        studentData.enrollment &&
                        studentData.enrollment.school_class
                          ? studentData.enrollment.school_class.name
                          : "—"
                      }}
                    </div>
                  </div>

                  <!-- Section -->
                  <div class="col-5 col-md-4 mb-3">
                    <div class="mb-1">Section:</div>
                    <div class="font-weight-bold">
                      {{
                        studentData.enrollment && studentData.enrollment.section
                          ? studentData.enrollment.section.name
                          : "—"
                      }}
                    </div>
                  </div>

                  <!-- Roll No -->
                  <div class="col-5 col-md-4 mb-3">
                    <div class="mb-1">Roll No:</div>
                    <div class="font-weight-bold">
                      {{
                        studentData.enrollment &&
                        studentData.enrollment.roll_no != null
                          ? studentData.enrollment.roll_no
                          : "—"
                      }}
                    </div>
                  </div>
                </div>

                <!-- Second Row -->
                <div class="row justify-content-center mb-2">
                  <!-- SIC -->
                  <div class="col-5 col-md-4 mb-3">
                    <div class="mb-1">Blood Group:</div>
                    <div class="font-weight-bold">
                      {{
                        studentData.enrollment &&
                        studentData.blood_group != null
                          ? studentData.blood_group
                          : "—"
                      }}
                    </div>
                  </div>

                  <!-- House -->
                  <div class="col-5 col-md-4 mb-3">
                    <div class="mb-1">House:</div>
                    <div class="font-weight-bold">
                      {{
                        studentData.enrollment &&
                        studentData.enrollment.house != null
                          ? studentData.enrollment.house.name
                          : "—"
                      }}
                    </div>
                  </div>

                  <div class="col-5 col-md-4 mb-6">
                    <div class="mb-1">SIC:</div>
                    <div class="font-weight-bold">
                      {{ jeeNeetStatus }}
                    </div>
                  </div>
                </div>
              </div>
            </a-card>
          </div>

          <!-- RIGHT CARD: “Student Details” with Edit/Delete in its header -->
          <div class="col-12 col-md-8 mb-3">
            <!-- Note the use of `#extra` instead of `slot="extra"`! -->
            <a-card :bordered="true" title="Student Details">
              <template slot="extra">
                <a
                  href="javascript:;"
                  v-if="rolesetting.editable"
                  class="btn btn-sm btn-primary btn-with-addon mr-2"
                  @click="handleEditClick(studentData)"
                >
                  <span class="btn-addon">
                    <i class="btn-addon-icon fe fe-edit" />
                  </span>
                  Edit
                </a>
                <a
                  href="javascript:;"
                  v-if="rolesetting.deletable"
                  class="btn btn-sm btn-danger btn-with-addon mr-2"
                  @click="onDelete(studentData)"
                >
                  <span class="btn-addon">
                    <i class="btn-addon-icon fe fe-trash" />
                  </span>
                  Delete
                </a>
              </template>
              <!-- Two-column grid of label/value pairs -->
              <a-tabs v-model="parentActiveKey" type="card">
                <a-tab-pane key="1" tab="Basic">
                  <div class="row">
                    <div
                      class="col-5 col-md-4 mb-3"
                      v-for="(item, relation) in parentInfo"
                      :key="'parent-' + relation"
                    >
                      <div class="detail-item">
                        <div class="d-flex align-items-center">
                          <span class="mb-1">{{ relation }}:</span>
                          <show-parent
                            v-if="item.first_name"
                            class="ml-2"
                            :parentData="item"
                          />
                        </div>
                        <div class="font-weight-bold">
                          {{ item.first_name || "—" }} {{ item.last_name }}
                        </div>
                      </div>
                    </div>
                    <div
                      class="col-5 col-md-4 mb-3"
                      v-for="(item, index) in basicInfo"
                      :key="index"
                    >
                      <div class="detail-item">
                        <div class="mb-1">{{ index }}</div>
                        <div class="font-weight-bold">{{ item || "—" }}</div>
                      </div>
                    </div>
                  </div>
                </a-tab-pane>
                <a-tab-pane key="2" tab="Contact">
                  <div class="row">
                    <div
                      class="col-5 col-md-4 mb-3"
                      v-for="(item, index) in contactInfo"
                      :key="index"
                    >
                      <div class="detail-item">
                        <div class="mb-1">{{ index }}</div>
                        <div class="font-weight-bold">{{ item || "—" }}</div>
                      </div>
                    </div>
                  </div>
                </a-tab-pane>
                <a-tab-pane key="3" tab="Academic">
                  <div class="pt-2">
                    <div class="row">
                      <div
                        class="col-12 mb-3"
                        v-if="classSubjects.normal_subjects && classSubjects.normal_subjects.length > 0"
                      >
                        <div class="mb-2">
                          <p class="h6 text-muted">Compulsary Subjects:</p>
                        </div>
                        <div class="d-flex flex-wrap">
                          <div
                            v-for="(classSubject, index) in classSubjects.normal_subjects"
                            :key="classSubject.id"
                            class="mr-2 mb-1"
                          >
                            <span class="font-weight-bold">
                              {{ classSubject.name }}{{ index !== classSubjects.normal_subjects.length - 1 ? ',' : '' }}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div
                        class="col-12 mb-3"
                        v-for="(value, groupName) in electivesInfo"
                        :key="groupName"
                      >
                        <div class="mb-2">
                          <p class="h6 text-muted">{{ groupName }}:</p>
                        </div>
                        <div class="d-flex flex-wrap">
                          <span class="font-weight-bold">
                            {{ value.join(', ') }}
                          </span>
                        </div>
                      </div>
                      <div
                        class="col-12 mb-3"
                        v-for="group in filteredGroupSubjects"
                        :key="group.group_subject_id"
                      >
                        <div class="mb-2">
                          <p class="h6 text-muted">{{ group.group_subject_name }}:</p>
                        </div>
                        <div class="d-flex flex-wrap">
                          <div
                            v-for="(sub, index) in group.group_subjects"
                            :key="sub.subject_id"
                            class="mr-2 mb-1"
                          >
                            <span class="font-weight-bold">
                              {{ sub.subject_name }}{{ index !== group.group_subjects.length - 1 ? ',' : '' }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <a-divider />
                  </div>
                  <div class="pt-2">
                    <div class="mb-2">
                      <p class="h6 text-muted">Previous Academic Details:</p>
                    </div>
                    <div class="row">
                      <div
                        class="col-5 col-md-4 mb-3"
                        v-for="(item, index) in previousSchoolInfo"
                        :key="index"
                      >
                        <div class="detail-item">
                          <div class="mb-1">{{ index }}</div>
                          <div class="font-weight-bold">{{ item || "—" }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </a-tab-pane>
                <a-tab-pane key="4" tab="Medical">
                  <div class="pt-2">
                    <div class="mb-2">
                      <p class="h6 text-muted">Medical History:</p>
                    </div>
                    <div class="row">
                      <div
                        class="col-6 col-md-4"
                        v-for="(item, index) in medicalInfo"
                        :key="index"
                      >
                        <div class="mb-1">{{ index }} :</div>
                        <div>
                          <strong class="font-weight-bold">{{
                            item || "—"
                          }}</strong>
                        </div>
                      </div>
                      <div class="col-12 text-right">
                        <medical-history
                          :medicalData="
                            studentData ? studentData.medical_history : ''
                          "
                        />
                      </div>
                    </div>
                  </div>
                </a-tab-pane>
                <a-tab-pane key="5" tab="Others">
                  <div class="pt-2">
                    <div class="mb-2">
                      <p class="h6 text-muted">Other Informations:</p>
                    </div>
                    <div class="row">
                      <div
                        class="col-12 col-sm-6 mb-3"
                        v-for="(item, index) in otherInfo"
                        :key="index"
                      >
                        <div class="mb-1">{{ index }}</div>
                        <div>
                          <strong class="font-weight-bold">{{
                            item || "—"
                          }}</strong>
                        </div>
                      </div>
                    </div>
                  </div>
                </a-tab-pane>
              </a-tabs>
            </a-card>
          </div>
          <!-- <div class="col-12 col-md-8"> -->
          <!-- <div class="row px-3">
                        <div>
                            <a-avatar shape="square" :size="96" :icon="studentData.photo ? '' : 'user'" :src="studentData.photo"/>
                        </div>
                        <div class="px-3">
                            <p class="h4 font-weight-bold mb-1">{{studentData.first_name}} {{studentData.last_name}}</p>
                            <p class="font-weight-bold mb-0">Student Code: {{studentData.student_code}}</p>
                            <p class="font-weight-bold mb-0">
                                Class: {{studentData.enrollment ? studentData.enrollment.school_class ? studentData.enrollment.school_class.name : '' : ''}}
                                <span class="mr-3"></span>
                                Section: {{studentData.enrollment ? studentData.enrollment.section ? studentData.enrollment.section.name : '' : ''}}
                            </p>
                            <p class="font-weight-bold mb-0">Roll No: {{studentData.enrollment ? studentData.enrollment.roll_no : ''}}</p>
                        </div>
                        <div class="col-md-6">

                      <div class="row ">
                            <div
                            class="col-5 col-md-4 mb-3"
                            v-for="(item, index) in basicInfo"
                            :key="index"
                            >
                            <div class="detail-item">
                            <div class="text-muted">{{ index }}</div>
                            <div class="font-weight-bold">{{ item || '—' }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                    </div> -->
          <!-- </div> -->
          <!-- <div class="col-12 col-md- text-right mt-3 mt-md-0"> -->
          <!-- <a href="javascript:;" class="btn btn-sm btn-dark btn-with-addon mr-2"
                    @click="handlePrint(studentData)"
                  >
                    <span class="btn-addon">
                      <i class="btn-addon-icon fe fe-printer" />
                    </span>
                    Print
                  </a> -->
          <!-- <a
              href="javascript:;"
              v-if="rolesetting.editable"
              class="btn btn-sm btn-primary btn-with-addon mr-2"
              @click="handleEditClick(studentData)"
            >
              <span class="btn-addon">
                <i class="btn-addon-icon fe fe-edit" />
              </span>
              Edit
            </a>
            <a
              href="javascript:;"
              v-if="rolesetting.deletable"
              class="btn btn-sm btn-danger btn-with-addon mr-2"
              @click="onDelete(studentData)"
            >
              <span class="btn-addon">
                <i class="btn-addon-icon fe fe-trash" />
              </span>
              Delete
            </a>
          </div> -->
        </div>
        <a-divider />
        <!-- Basic Details -->
        <!-- <div class="pt-2">
                <div class="mb-2">
                    <p class="h6 text-muted">Basic Details:</p>
                </div>
                <div class="row">
                    <div class="col-6 col-md-4 pb-3" v-for="(item, index) in basicInfo" :key="index">
                        <div class="mb-1">{{index}} :</div>
                        <div>
                            <strong class="h5 font-weight-bold">{{item}}</strong>
                        </div>
                    </div>
                </div>
                <a-divider />
            </div> -->
        <!-- Electives Details -->
        <!-- <div class="pt-2">
          <div class="mb-2">
            <p class="h6 text-muted">Elective Subjects:</p>
          </div>
          <div class="row">
            <div
              class="col-12 col-md-6 pb-3"
              v-for="(item, index) in electivesInfo"
              :key="index"
            >
              <div class="mb-1">
                {{ index }} :
                <strong class="h5 font-weight-bold ml-3">{{ item }}</strong>
              </div>
            </div>
          </div>
          <a-divider />
        </div> -->
        <a-tabs v-model="childActiveKey" type="card">
          <a-tab-pane key="1-1" tab="Attendance">
            <!-- Attendance Report Content -->
            <div class="pt-2">
              <div class="mb-4">
                <p class="h5 font-weight-bold text-muted">Attendance Report</p>
              </div>
              <div class="row justify-content-start align-items-center">
                <!-- Attendance Stats -->
                <div class="col-12 col-md-7">
                  <div class="row text-center">
                    <div class="col-6 col-sm-3 mb-3">
                      <div class="mb-1 text-muted">Total Days</div>
                      <div class="h5 font-weight-bold">
                        {{ attendanceData.totalDays }}
                      </div>
                    </div>
                    <div class="col-6 col-sm-3 mb-3">
                      <div class="mb-1 text-muted">Present</div>
                      <div class="h5 font-weight-bold text-success">
                        {{ attendanceData.present }}
                      </div>
                    </div>
                    <div class="col-6 col-sm-3 mb-3">
                      <div class="mb-1 text-muted">Absent</div>
                      <div class="h5 font-weight-bold text-danger">
                        {{ attendanceData.absent }}
                      </div>
                    </div>
                    <div class="col-6 col-sm-3 mb-3">
                      <div class="mb-1 text-muted">Leave</div>
                      <div class="h5 font-weight-bold">
                        {{ attendanceData.leave }}
                      </div>
                    </div>
                    <div class="col-6 col-sm-3 mb-3">
                      <div class="mb-1 text-muted">Holidays</div>
                      <div class="h5 font-weight-bold">
                        {{ attendanceData.holidays }}
                      </div>
                    </div>
                    <div class="col-6 col-sm-3 mb-3">
                      <div class="mb-1 text-muted">Actual Days</div>
                      <div class="h5 font-weight-bold">
                        {{ attendanceData.actualDays }}
                      </div>
                    </div>
                    <div class="col-6 col-sm-3 mb-3">
                      <div class="mb-1 text-muted">Pending Days</div>
                      <div class="h5 font-weight-bold">
                        {{ attendanceData.pendingDays }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Percentage Graph -->
                <div
                  class="col-12 col-md-4 d-flex justify-content-center align-items-center"
                >
                  <div class="attendance-chart-container">
                    <svg
                      :width="chartSize"
                      :height="chartSize"
                      viewBox="0 0 100 100"
                    >
                      <circle
                        class="chart-background"
                        cx="50"
                        cy="50"
                        :r="radius"
                        :stroke-width="strokeWidth"
                      ></circle>
                      <circle
                        class="chart-progress"
                        cx="50"
                        cy="50"
                        :r="radius"
                        :stroke-width="strokeWidth"
                        :stroke-dasharray="circumference"
                        :stroke-dashoffset="progressOffset"
                        transform="rotate(-90 50 50)"
                      ></circle>
                      <text
                        x="50"
                        y="50"
                        text-anchor="middle"
                        dominant-baseline="middle"
                        :style="{
                          fontSize: '24px',
                          fontWeight: 'bold',
                          fill: '#333',
                        }"
                        class="chart-percentage-text"
                      >
                        {{ attendancePercentage }}%
                      </text>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </a-tab-pane>
          <a-tab-pane key="1-2" tab="Fees">
            <div class="pt-2">
              <div class="mb-4">
                <p class="h5 font-weight-bold text-muted">Fee Details</p>
              </div>
              <a-table
                :columns="feeColumns"
                :data-source="feeData"
                :pagination="false"
                rowKey="id"
              >
                <span slot="status" slot-scope="status">
                  <a-tag :color="getStatusColor(status)">
                    {{ status.toUpperCase() }}
                  </a-tag>
                </span>
              </a-table>
            </div>
          </a-tab-pane>
          <a-tab-pane key="1-3" tab="Exam Results">
            <div class="pt-2">
              <div class="mb-4">
                <p class="h5 font-weight-bold text-muted">Exam Results</p>
              </div>

              <a-skeleton active :loading="examResultLoading" />

              <div v-if="!examResultLoading">
                <div v-if="examResult.length === 0" class="text-center py-4">
                  <a-empty description="No exam results available" />
                </div>

                <a-collapse v-model="activeExams" accordion v-else>
                  <a-collapse-panel
                    v-for="(exam, index) in examResult"
                    :key="index"
                    :header="examHeader(exam)"
                    class="exam-panel"
                  >
                    <div class="exam-info mb-3">
                      <a-row :gutter="16">
                        <a-col :span="8">
                          <div class="exam-detail">
                            <strong>Date:</strong>
                            {{ formatDate(exam.start_date) }} to
                            {{ formatDate(exam.end_date) }}
                          </div>
                        </a-col>
                        <a-col :span="8">
                          <div class="exam-detail">
                            <strong>Status:</strong>
                            <a-tag :color="exam.declared ? 'green' : 'orange'">
                              {{ exam.declared ? "Declared" : "Not Declared" }}
                            </a-tag>
                          </div>
                        </a-col>
                        <a-col :span="8">
                          <div class="exam-detail">
                            <strong>Grading:</strong>
                            {{ exam.grades ? "With Grades" : "Marks Only" }}
                          </div>
                        </a-col>
                      </a-row>
                    </div>

                    <div class="subject-table">
                      <a-table
                        :columns="subjectColumns"
                        :data-source="getSubjectRows(exam)"
                        :pagination="false"
                        size="small"
                        rowKey="subject_id"
                      >
                        <template slot="subject" slot-scope="text, record">
                          <div class="subject-name">
                            {{ record.subject.name }}
                            <span class="subject-abbr">{{
                              record.subject.abbreviation
                            }}</span>
                          </div>
                        </template>

                        <template slot="full_mark" slot-scope="text">
                          <div v-if="text">{{ text }}</div>
                          <div v-else class="no-marks-text">—</div>
                        </template>

                        <template slot="pass_mark" slot-scope="text">
                          <div v-if="text">{{ text }}</div>
                          <div v-else class="no-marks-text">—</div>
                        </template>

                        <template slot="theory_mark" slot-scope="text, record">
                          <div v-if="record.is_absent">Absent</div>
                          <div v-else-if="record.theory_mark">{{ record.theory_mark}}</div>
                        </template>

                        <template slot="practical_mark" slot-scope="text, record">
                          <div v-if="record.is_absent">Absent</div>
                          <div v-else-if="record.practical_mark">{{ record.practical_mark }}</div>
                        </template>

                        <template slot="obtained" slot-scope="text, record">
                          <div v-if="record.is_absent">Absent</div>
                          <div v-else-if="record.obtained_mark">{{ record.obtained_mark }}</div>
                          <!-- <div v-else class="no-marks-text">Not Available</div> -->
                        </template>

                        <template slot="status" slot-scope="text, record">
                          <a-tag :color="record.status === 'Pass' ? 'green' : 'red'">
                            {{ record.status }}
                          </a-tag>
                        </template>
                      </a-table>
                    </div>

                    <!-- Summary row -->
                    <div class="exam-summary mt-3 p-3 bg-light">
                      <a-row :gutter="16">
                      <!-- Total Marks -->
                      <a-col :span="6">
                      <div class="summary-item">
                        <strong>Total Marks:</strong>
                        <span>{{ exam.student_exams[0] && exam.student_exams[0].total_mark || '—' }}</span>
                        </div>
                      </a-col>
                      <!-- Percentage -->
                      <a-col :span="6">
                      <div class="summary-item">
                        <strong>Percentage:</strong>
                          <span>{{ exam.student_exams[0] && exam.student_exams[0].mark_percentage || '—' }}%</span>
                        </div>
                      </a-col>

                      <!-- Rank -->
                      <a-col :span="6">
                      <div class="summary-item">
                      <strong>Rank:</strong>
                          <span>{{ exam.student_exams[0] && exam.student_exams[0].class_rank || '—' }}</span>
                        </div>
                      </a-col>

                      <!-- Result -->
                      <a-col :span="6">
                      <div class="summary-item">
                        <strong>Result:</strong>
                        <a-tag :color="(exam.student_exams[0] && exam.student_exams[0].status) === 'Pass' ? 'green' : 'red'">
                          {{ exam.student_exams[0] && exam.student_exams[0].status || 'Pending' }}
                        </a-tag>
                      </div>
                      </a-col>
                      </a-row>
                    </div>
                  </a-collapse-panel>
                </a-collapse>
              </div>
            </div>
          </a-tab-pane>
          <!-- Add this new Discipline tab -->
          <a-tab-pane key="1-4" tab="Discipline">
             <div class="mb-4">
                <p class="h5 font-weight-bold text-muted">Student Discipline</p>
              </div>
            <student-discipline-tab
              :studentId="studentId"
              :studentInfo="studentData"
            />
          </a-tab-pane>
        </a-tabs>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { find } from 'lodash'
import { mapState } from 'vuex'
import moment from 'moment'
import ShowParent from '../Parent/index'
import MedicalHistory from '../Medical/index'
import { Card, Tabs } from 'ant-design-vue'
import StudentDisciplineTab from '@/components/student/Display/studentDiscipline'
import {
  basicInfo,
  contactInfo,
  academicInfo,
  electivesInfo,
  previousSchoolInfo,
  parentInfo,
  medicalInfo,
  otherInfo,
} from './data'

export default {
  name: 'StudentInfo',
  components: {
    ShowParent,
    MedicalHistory,
    StudentDisciplineTab,
    'a-card': Card,
    'a-tabs': Tabs,
    'a-tab-pane': Tabs.TabPane,
  },
  data: function () {
    return {
      form: this.$form.createForm(this),
      loading: false,
      visible: false,
      schoolSessions: [],
      studentData: {},
      basicInfo: {},
      contactInfo: {},
      parentActiveKey: '1',
      childActiveKey: '1-1',
      academicInfo: {},
      electivesInfo: {},
      attendanceData: {},
      classSubjects: {
        normal_subjects: [],
        group_subjects: [],
        elective_subjects: [
          {
            electives: [],
          },
        ],
      },
      chartSize: 140, // Size of the SVG canvas (width and height)
      strokeWidth: 10, // Thickness of the progress circle
      radius: 40, // Radius of the circle
      previousSchoolInfo: {},
      parentInfo: {},
      medicalInfo: {},
      otherInfo: {},
      currentSession: '',
      feeData: [],
      subjectColumns: [
        {
          title: 'Subject',
          key: 'subject',
          scopedSlots: { customRender: 'subject' },
          width: '35%',
        },
        {
          title: 'Full Marks',
          dataIndex: 'full_mark',
          key: 'full_mark',
          align: 'center',
          scopedSlots: { customRender: 'full_mark' },
        },
        {
          title: 'Pass Marks',
          dataIndex: 'pass_mark',
          key: 'pass_mark',
          align: 'center',
          scopedSlots: { customRender: 'pass_mark' },
        },
        {
          title: 'Theory',
          dataIndex: 'theory_mark',
          key: 'theory_mark',
          align: 'center',
        },
        {
          title: 'Practical',
          dataIndex: 'practical_mark',
          key: 'practical_mark',
          align: 'center',
        },
        {
          title: 'Obtained',
          key: 'obtained',
          align: 'center',
          scopedSlots: { customRender: 'obtained' },
        },
        {
          title: 'Status',
          dataIndex: 'status',
          key: 'status',
          align: 'center',
          scopedSlots: { customRender: 'status' },
        },

      ],
      // Columns for the Fees Table
      feeColumns: [
        {
          title: 'Type',
          dataIndex: 'type',
          key: 'type',
        },
        {
          title: 'Amount',
          dataIndex: 'amount',
          key: 'amount',
          customRender: (text) => `₹${text.toLocaleString('en-IN')}`,
        },
        {
          title: 'Discount Amount',
          dataIndex: 'discountAmount',
          key: 'discountAmount',
          customRender: (text) => `₹${text.toLocaleString('en-IN')}`,
        },
        {
          title: 'Discount (%)',
          dataIndex: 'discountPercentage',
          key: 'discountPercentage',
          customRender: (text) => `${text}%`,
        },
        {
          title: 'Due Date',
          dataIndex: 'dueDate',
          key: 'dueDate',
        },
        {
          title: 'Fine',
          dataIndex: 'fine',
          key: 'fine',
        },
        {
          title: 'Total Amount',
          dataIndex: 'totalAmount',
          key: 'totalAmount',
          customRender: (text) => `₹${text.toLocaleString('en-IN')}`,
        },
        {
          title: 'Status',
          dataIndex: 'status',
          key: 'status',
          scopedSlots: { customRender: 'status' },
          filters: [
            { text: 'Pending', value: 'Pending' },
            { text: 'Paid', value: 'Paid' },
            { text: 'Overdue', value: 'Overdue' },
          ],
          onFilter: (value, record) => record.status.indexOf(value) === 0,
        },
      ],
    }
  },
  props: ['session', 'rolesetting'],
  // props: {
  //   session: { type: Number },
  //   rolesetting: { type: Object },
  // },
  computed: {
    ...mapState(['settings', 'user']),
    jeeNeetStatus() {
      const Jee = this.studentData.jee
      const Neet = this.studentData.neet

      if (Jee && Neet) {
        return 'JEE & NEET'
      } else if (Jee) {
        return 'JEE'
      } else if (Neet) {
        return 'NEET'
      } else {
        return 'Not Enrolled'
      }
    },
    getStatusColor() {
      return (status) => {
        const colorMap = {
          Paid: 'green',
          Pending: 'volcano',
          Overdue: 'red',
          'Partially Paid': 'orange',
        }
        return colorMap[status] || 'default'
      }
    },
    // Computed property for attendance percentage
    attendancePercentage() {
      if (this.attendanceData.totalDays === 0) {
        return 0
      }
      return Math.round(
        (this.attendanceData.present / this.attendanceData.totalDays) * 100
      )
    },
    // Computed property for SVG circumference
    circumference() {
      return 2 * Math.PI * this.radius
    },
    // Computed property for SVG progress offset
    progressOffset() {
      return (
        this.circumference -
        (this.attendancePercentage / 100) * this.circumference
      )
    },
    filteredGroupSubjects() {
      return (this.classSubjects.group_subjects || []).filter(
        (group) => group.group_subjects && group.group_subjects.length > 0
      )
    },
  },
  mounted: function () {
    this.$http
      .get('/api/session')
      .then(({ data }) => {
        this.schoolSessions = data.data
        this.currentSession = find(this.schoolSessions, ['is_current', 1])
      })
      .catch((error) => {
        console.log(error)
      })
    this.$eventBus.$on('openStudentShowModal', this.showModal)
  },
  methods: {
    examHeader(exam) {
      return `${exam.exam.name} - ${this.formatDate(exam.start_date)}`
    },

    formatDate(dateString) {
      return moment(dateString).format('DD MMM YYYY')
    },

    getSubjectRows(exam) {
      const studentExamMarks = exam.student_exams && exam.student_exams.length > 0 ? exam.student_exams[0].student_exam_marks : []
      const subjectRows = exam.class_section_exam_subject.map(subject => {
        const markData = studentExamMarks.find(m => m.subject_id === subject.subject_id)
        return {
          ...subject,
          full_mark: subject.full_mark || '—',
          pass_mark: subject.pass_mark || '—',
          obtained_mark: markData ? markData.mark : '—',
          theory_mark: markData ? markData.theory_mark : '—',
          practical_mark: markData ? markData.practical_mark : '—',
          is_absent: markData ? markData.is_absent : 0,
          status: markData
            ? markData.is_failed === 1
              ? 'Fail'
              : 'Pass'
            : '—',
        }
      })
      return subjectRows
    },
    fetchExamResults(enrollmentId, studentSessionId) {
      this.examResultLoading = true
      this.$http
        .get(
          `/api/reports/student/exam-result?enrollment_id=${enrollmentId}&session_id=${studentSessionId}`
        )
        .then(({ data }) => {
          this.examResult = data.data
          console.log('exam Result data:', this.examResult)
        })
        .catch((error) => {
          console.log('Error fetching exam results:', error)
        })
        .finally(() => {
          this.examResultLoading = false
        })
    },

    fetchExamFee(enrollmentId, studentSessionId) {
      this.$http
        .get(
          `/api/student_fee_report?enrollment_id=${enrollmentId}&session_id=${studentSessionId}&limit=1&page=1`
        )
        .then(({ data }) => {
          this.feeData = data
          if (data.length > 0 && data[0].student_fees) {
            this.feeData = data[0].student_fees.map((fee) => ({
              id: fee.id || '',
              type: fee.fee.name || 'Exam Fee',
              amount: fee.amount || 0,
              discountAmount: fee.discount_amount || 0,
              discountPercentage: fee.discount_percentage || 0,
              dueDate: fee.due_date || '',
              fine: fee.fine || 0,
              totalAmount: fee.fee_total || 0,
              status: this.mapPaymentStatus(fee.payment_status),
            }))
          } else {
            this.examFeeData = {
              type: 'Exam Fee',
              amount: 0,
              discountAmount: 0,
              discountPercentage: 0,
              dueDate: '',
              fine: 0,
              status: 'Pending',
            }
          }
          console.log('exam fee data:', this.feeData)
        })
        .catch((error) => {
          console.log('Error fetching exam fee :', error)
        })
    },
    mapPaymentStatus(statusCode) {
      // Map numeric status to human-readable text
      const statusMap = {
        0: 'Pending',
        1: 'Paid',
        2: 'Overdue',
        3: 'Partially Paid',
        // Add more status codes as needed
      }
      return statusMap[statusCode] || 'Unknown'
    },
    fetchAttendance(enrollmentId, studentSessionId) {
      this.$http
        .get(
          `/api/attendance/report?enrollment_id=${enrollmentId}&session_id=${studentSessionId}&startDate=&endDate=&limit=1&page=1`
        )
        .then(({ data }) => {
          this.attendanceData = data
          if (data.students && data.students.length > 0) {
            const student = data.students[0].student
            this.attendanceData = {
              present: student.present_days_count || 0,
              absent: student.absent_days_count || 0,
              leave: student.leave_days_count || 0,
              holidays: data.totalHolidays || 0,
              actualDays: data.actualDays || 0,
              pendingDays: student.total_pending_days || 0,
              totalDays: data.totalSchoolDays || 0,
            }
          } else {
            this.attendanceData = {
              present: 0,
              absent: 0,
              leave: 0,
              holidays: 0,
              actualDays: 0,
              pendingDays: 0,
              totalDays: 0,
            }
          }
          console.log('attendance data:', this.attendanceData)
        })
        .catch((error) => {
          console.log('Error fetching attendance :', error)
        })
        .finally(() => {
          this.loading = false
        })
    },

    fetchClassData(classId, studentSessionId) {
      this.$http
        .get(`/api/section/class/${classId}?session_id=${studentSessionId}`)
        .then(({ data }) => {
          this.sections = data.data // Assign to the 'sections' data property
          return this.$http.get(`/api/subject/class/${classId}`)
        })
        .then(({ data }) => {
          this.classSubjects = data.data // Assign to the 'classSubjects' data property
          console.log('classSubjects after assignment:', this.classSubjects)
        })
        .catch((error) => {
          console.log('Error fetching sections or subjects:', error)
        })
        .finally(() => {
          this.loading = false // Set loading to false after both calls are complete or an error occurs
        })
    },
    showModal(studentId) {
      this.studentId = studentId
      this.visible = true
      this.loading = true
      this.$http
        .get(`/api/student/${this.studentId}`, {
          params: {
            session_id: this.session,
          },
        })
        .then(({ data }) => {
          this.studentData = data.data
          const enrollmentId = this.studentData.enrollment.id
          const classId = this.studentData.enrollment.class_id
          const studentSessionId = this.studentData.enrollment.session_id
          this.basicInfo = basicInfo(data.data)
          this.contactInfo = contactInfo(data.data.contact)
          this.academicInfo = academicInfo(
            data.data.enrollment,
            this.currentSession
          )
          this.electivesInfo = electivesInfo(
            data.data.student_elective_subjects
          )
          this.previousSchoolInfo = previousSchoolInfo(data.data)
          this.parentInfo = parentInfo(data.data.student_parents)
          this.medicalInfo = medicalInfo(data.data)
          this.otherInfo = otherInfo(data.data)
          this.fetchClassData(classId, studentSessionId)
          this.fetchAttendance(enrollmentId, studentSessionId)
          this.fetchExamFee(enrollmentId, studentSessionId)
          this.fetchExamResults(enrollmentId, studentSessionId)
        })
        .catch((error) => {
          console.log(error)
          this.loading = false
        })
    },
    handlePrint(student) {
      console.log('Print')
    },
    handleEditClick(student) {
      this.$emit('editClick', student.id)
      this.visible = false
    },
    onDelete: function (record) {
      this.$confirm({
        title: `Are you sure?`,
        content: `Clicking Yes will delete ${record.first_name} ${record.last_name}?`,
        okText: 'Yes',
        okType: 'danger',
        cancelText: 'No',
        onOk: () => {
          this.$nprogress.start()
          this.$http
            .delete('/api/student/' + record.id)
            .then(() => {
              this.$emit('updateTable')
              this.$nprogress.done()
              this.$notification['success']({
                message: 'Deleted Successfully!',
                description:
                  record.first_name +
                  ' ' +
                  record.last_name +
                  ' has been deleted.',
              })
              this.visible = false
            })
            .catch((error) => {
              this.$nprogress.done()
              this.$notification['error']({
                message: error.message,
                description: 'Unable to delete student. Please try again.',
              })
            })
        },
        onCancel: () => {
          console.log('Cancel')
        },
      })
    },
    handleCancel(e) {
      this.visible = false
    },
  },
}
</script>
<style scoped>
/* Basic styling for the chart */
.attendance-chart-container {
  position: relative;
  width: 130px; /* Same as chartSize */
  height: 130px; /* Same as chartSize */
  margin: 0 auto; /* Center the chart */
}

.chart-background {
  fill: none;
  stroke: #e0e0e0;
}

.chart-progress {
  fill: none;
  stroke: #1890ff; /* Ant Design primary blue */
  stroke-linecap: round;
  transition: stroke-dashoffset 0.5s ease-in-out; /* Smooth animation for progress */
}

.chart-percentage-text {
  font-size: 24px; /* Adjust font size as needed */
  font-weight: bold;
  fill: #333; /* Darker text for readability */
}

/* General styling for attendance stats */
.text-success {
  color: #52c41a; /* Ant Design green */
}

.text-danger {
  color: #ff4d4f; /* Ant Design red */
}
.exam-panel {
  margin-bottom: 16px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.09);
}

.exam-info {
  padding: 12px 16px;
  background-color: #f8f9fa;
  border-radius: 4px;
  margin-bottom: 16px;
}

.exam-detail {
  margin-bottom: 8px;
}

.subject-table {
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  overflow: hidden;
}

.subject-name {
  display: flex;
  align-items: center;
}

.subject-abbr {
  background-color: #1890ff;
  color: white;
  border-radius: 4px;
  padding: 1px 6px;
  font-size: 11px;
  font-weight: bold;
  margin-left: 8px;
}

.no-marks-text {
  color: #bfbfbf;
  font-style: italic;
}

.exam-summary {
  border-radius: 4px;
  border: 1px solid #e8e8e8;
}

.summary-item {
  display: flex;
  justify-content: space-between;
}

/* Customize table header */
.subject-table >>> .ant-table-thead > tr > th {
  background-color: #f0f2f5;
  font-weight: 600;
}

/* Add hover effect to table rows */
.subject-table >>> .ant-table-tbody > tr:hover > td {
  background-color: #fafafa !important;
}

/* Add stripe effect to table */
.subject-table >>> .ant-table-tbody > tr:nth-child(even) {
  background-color: #f9f9f9;
}

/* Center align all content in table cells */
.subject-table >>> .ant-table-tbody > tr > td {
  text-align: center;
}

.subject-table >>> .ant-table-tbody > tr > td:first-child {
  text-align: right;
}
</style>
